import App from "@bloglovin/inspire-engine/App";
import { contextualAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import { SmartFeedEntityDynamicElementMixin } from "%/dynamic-component-editor/mixins/smart-feed-entity-dynamic-element-mixin";
import { readBoundContextName, resolveBoundEntityId } from "%/dynamic-component-editor/helpers/bound-context-entity-id";

// SubscriptionPlan has no dedicated DCE-tree entity store the way show /
// episode / event cards do (there is no single-plan-by-id GET route). But a
// FeedV2 `subscriptionPlans` feed already materializes each plan's full record
// ({ id, name, price, description, billingFrequency, … }) into the engine App's
// entity registry as it lists the feed page. So resolve the real plan straight
// from the engine App by the scoped feed-row context's pk, mirroring how the
// generic field-element path reads App.getEntityField. Falls back to the demo
// entity when no context is bound (editor preview, non-feed page, anon).
const SUBSCRIPTION_ENTITY_TYPE = "SubscriptionPlan";

export const SubscriptionCardElementMixin = {
  extends: SmartFeedEntityDynamicElementMixin,
  inject: {
    // Page-context id ({ subscriptionPlan: <id> }) when a plan card sits on a
    // plan page; feed-iterated cards fall through to the scoped context below.
    pageEntities: { default: null },
    // The nearest feed row's scoped contexts (AppComponentOverrideHost provides
    // this per row via ScopedProvidesContextsMixin). A FeedV2 subscription card
    // renders inside a feed, where the row's plan arrives as a scoped
    // `subscriptionPlan` context carrying the pk under entityPkUrlVars.id.
    scopedProvidedContexts: { default: null },
  },
  computed: {
    sessionSubscriptionPlanId() {
      if (typeof window === "undefined") {
        return 0;
      }
      const planId = Number(window.blclient?.data?.subscriberData?.subscription_plan_id);
      return Number.isFinite(planId) && planId > 0 ? planId : 0;
    },
    isCurrentPlan() {
      if (this.editMode) {
        return false;
      }
      const planId = this.sessionSubscriptionPlanId;
      if (!planId) {
        return false;
      }
      return planId === this.resolveSubscriptionPlanId();
    },
    boundContextName() {
      return readBoundContextName(this);
    },
    subscriptionEntityId() {
      return resolveBoundEntityId(this, "subscriptionPlan");
    },
  },
  methods: {
    resolveSubscriptionPlanId() {
      const fromEntity = Number(this.entity?.id);
      if (Number.isFinite(fromEntity) && fromEntity > 0) {
        return fromEntity;
      }
      const fromLegacyShape = Number(this.entity?.subscriptionPlanId);
      if (Number.isFinite(fromLegacyShape) && fromLegacyShape > 0) {
        return fromLegacyShape;
      }
      return Number(this.subscriptionEntityId) || 0;
    },
    getExampleEntity() {
      return {
        subscriptionPlanId: 0,
        creatorOrgId: 0,
        publicationId: 0,
        name: "Demo Subscription Plan",
        description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
        billingFrequency: "monthly",
        price: 9.99,
        currencyCode: "USD",
        accessRevocationGracePeriodDays: 7,
        exclusivityWindowDays: 30,
        includedFeatures: ["Exclusive access to content"],
        excludedFeatures: ["Access to exclusive community"],
        status: "active",
        dateCreated: 1733136252,
        dateUpdated: 1733136252,
      };
    },
    getRealEntity() {
      const id = this.subscriptionEntityId;
      if (!id) {
        return this.getExampleEntity();
      }
      // contextualAppName() is always a booted app (builder or render), so no try/catch.
      const entity = App.get(contextualAppName()).getEntity(SUBSCRIPTION_ENTITY_TYPE, { id });
      if (entity && (entity.name !== undefined || entity.price !== undefined)) {
        return entity;
      }
      return this.getExampleEntity();
    },
  },
};
